import { Box, Divider, Typography } from '@mui/material';
import { FunctionComponent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { ColorPalette } from 'shared/consts/colorPalette';
import { TabButton } from 'shared/ui/TabButton';
import { Loader } from 'shared/ui/Loader';
import { useUserDetailsInfoStore } from 'widgets/admin/UserDetails/state';
import {
	PersonalInfo,
	Subscriptions,
	Billing,
	CoUsers,
	AdditionalFeatures,
} from 'widgets/admin/UserDetails/ui/tabs';
import { Button } from 'shared/ui/Button';
import { UnlimitedUserSubscription } from './modals/UnlimitedUserSubscriptionsModal';
import { PatchUnlimitedSubscription, getAdminUser, impersonateUser } from 'entities/admin/Users/api';
import { IMPERSONATION_TOKEN_PARAM } from 'shared/helpers/impersonation';
import { enqueueSnackbar } from 'notistack';
import { AxiosError } from 'axios';
import { showErrorNotification } from 'shared/helpers/showErrorNotification';
import { userDetailsTabs } from 'widgets/admin/UserDetails/types';

const tabsArray = [
	{ name: userDetailsTabs.info, text: 'admin.users.details.tabs.tab_1' },
	{ name: userDetailsTabs.subscribtions, text: 'admin.users.details.tabs.tab_2' },
	{ name: userDetailsTabs.billing, text: 'admin.users.details.tabs.tab_3' },
	{ name: userDetailsTabs.coUsers, text: 'admin.users.details.tabs.tab_4' },
	{ name: userDetailsTabs.additionalFeatures, text: 'admin.users.labels.additional_features' },
];

export const UserDetailsInfo: FunctionComponent = () => {
	const { t } = useTranslation();
	const { userId } = useParams();

	const { activeTab, setSelectedUser, changeActiveTab } = useUserDetailsInfoStore();

	const [isOpenModal, setIsOpenModal] = useState<boolean>(false);

	const { data: userInfo } = useQuery({
		queryKey: ['get/admin/user'],
		queryFn: () => getAdminUser(userId),
		onSuccess: (response) => {
			setSelectedUser(response.data);
		},
		onError: (err) => {
			showErrorNotification(err as AxiosError);
		},
	});

	const subTitle = useMemo(
		() =>
			userInfo?.data?.firstName && userInfo?.data?.lastName
				? `${userInfo?.data.firstName} ${userInfo?.data.lastName} - "${userInfo?.data.email}"`
				: `"${userInfo?.data?.email}"`,
		[userInfo],
	);

	const role = useMemo(
		() =>
			userInfo?.data?.role &&
			`${userInfo?.data?.role}`.charAt(0) + `${userInfo?.data?.role}`.slice(1).toLowerCase(),
		[userInfo],
	);

	const handleImpersonate = async (userID: string) => {
		// the tab must be opened synchronously in the click handler,
		// otherwise popup blockers may reject window.open after await
		const newTab = window.open('', '_blank');
		try {
			const response = await impersonateUser(userID);
			const impersonationUrl = `/?${IMPERSONATION_TOKEN_PARAM}=${encodeURIComponent(
				response.data.accessToken,
			)}`;
			if (newTab) {
				newTab.location.href = impersonationUrl;
			} else {
				window.open(impersonationUrl, '_blank');
			}
		} catch (err) {
			newTab?.close();
			showErrorNotification(err as AxiosError, t('admin.users.snackbar.error_impersonate'));
		}
	};

	const handleApplyUnlimited = async (userID: string) => {
		try {
			await PatchUnlimitedSubscription(userID);
			setIsOpenModal(false);
			enqueueSnackbar({
				message: t('admin.users.snackbar.success_update_unlimited'),
				variant: 'success',
			});
		} catch (err) {
			showErrorNotification(err as AxiosError, t('admin.users.snackbar.error_update_unlimited'));
		}
	};

	if (!userInfo?.data) return <Loader />;

	return (
		<>
			<UnlimitedUserSubscription
				user={userInfo.data}
				open={isOpenModal}
				handleClose={() => setIsOpenModal(false)}
				handleApply={() => handleApplyUnlimited(userInfo.data._id)}
			/>
			<Box
				sx={{
					display: 'flex',
					width: '100%',
					justifyContent: 'space-between',
					alignItems: 'center',
				}}
			>
				<Box>
					<Typography variant={'h4'} color={ColorPalette.astronaut}>
						{t('admin.users.details.title')}
					</Typography>
					<Box sx={{ display: 'flex', gap: '30px' }}>
						<Typography
							variant={'subtitle1'}
							color={ColorPalette.baliHai}
							sx={{ marginTop: '24px' }}
						>
							{subTitle}
						</Typography>
						<Typography
							variant={'subtitle1'}
							color={ColorPalette.baliHai}
							sx={{ marginTop: '24px' }}
						>
							{t('admin.users.details.role')} {role}
						</Typography>
					</Box>
				</Box>
				<Box sx={{ display: 'flex', gap: '16px' }}>
					<Button size="medium" onClick={() => handleImpersonate(userInfo.data._id)}>
						{t('admin.users.details.login_as_user')}
					</Button>
					<Button size="medium" onClick={() => setIsOpenModal(true)}>
						{t('admin.users.details.apply_unlimited')}
					</Button>
				</Box>
			</Box>

			<Divider color={ColorPalette.periwinkle} sx={{ margin: '44px 0' }} />
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'flex-start',
					alignItems: 'center',
					gap: '20px',
					marginBottom: '44px',
				}}
			>
				{tabsArray.map((item, i) => (
					<TabButton
						key={`${item.name}=${i}`}
						label={t(`${item.text}`)}
						isActive={activeTab === item.name}
						action={() => changeActiveTab(item.name)}
					/>
				))}
			</Box>
			{activeTab === userDetailsTabs.info && <PersonalInfo user={userInfo.data} />}
			{activeTab === userDetailsTabs.subscribtions && <Subscriptions />}
			{activeTab === userDetailsTabs.billing && <Billing user={userInfo.data} />}
			{activeTab === userDetailsTabs.coUsers && <CoUsers user={userInfo.data} />}
			{activeTab === userDetailsTabs.additionalFeatures && <AdditionalFeatures user={userInfo.data} />}
		</>
	);
};
